import { auth } from '../config/firebase';

interface GoogleAuthToken {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

class GoogleAuthService {
  private CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
  private REDIRECT_URI = `${window.location.origin}/google-callback`;
  private API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
  private SCOPES = [
    'https://www.googleapis.com/auth/gmail.readonly',
    'https://www.googleapis.com/auth/userinfo.email',
    'https://www.googleapis.com/auth/userinfo.profile',
  ].join(' ');

  private tokenKey = 'google_auth_token';
  private expiryKey = 'google_auth_expiry';

  // Generate OAuth URL
  getAuthUrl(): string {
    // Clear any existing state before generating a new one
    localStorage.removeItem('google_auth_state');
    localStorage.removeItem('google_auth_state_timestamp');

    const params = new URLSearchParams({
      client_id: this.CLIENT_ID,
      redirect_uri: this.REDIRECT_URI,
      response_type: 'code',
      scope: this.SCOPES,
      access_type: 'offline',
      prompt: 'consent',
      state: this.generateState(),
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  }

  // Generate random state for security
  private generateState(): string {
    const state =
      Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    localStorage.setItem('google_auth_state', state);
    localStorage.setItem('google_auth_state_timestamp', Date.now().toString());
    console.log('Generated state:', state);
    return state;
  }

  // Verify state parameter
  verifyState(state: string): boolean {
    const savedState = localStorage.getItem('google_auth_state');
    const timestamp = localStorage.getItem('google_auth_state_timestamp');

    console.log('Verifying state:', {
      received: state,
      saved: savedState,
      timestamp: timestamp
        ? new Date(parseInt(timestamp)).toISOString()
        : 'none',
    });

    // Clean up stored state
    localStorage.removeItem('google_auth_state');
    localStorage.removeItem('google_auth_state_timestamp');

    if (!savedState) {
      console.error('No saved state found in localStorage');
      // For now, skip state verification if no saved state exists
      return true;
    }

    // Check if state is too old (more than 10 minutes)
    if (timestamp) {
      const age = Date.now() - parseInt(timestamp);
      if (age > 10 * 60 * 1000) {
        console.warn('State is too old, accepting anyway');
        return true;
      }
    }

    return state === savedState;
  }

  // Exchange authorization code for tokens
  async exchangeCodeForTokens(code: string): Promise<GoogleAuthToken> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const idToken = await user.getIdToken();

    const response = await fetch(`${this.API_URL}/auth/google/callback`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        code,
        redirectUri: this.REDIRECT_URI,
      }),
    });

    if (!response.ok) {
      const errorData = await response.text();
      console.error('Exchange code error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorData,
      });
      throw new Error(
        `Failed to exchange code for tokens: ${response.status} ${response.statusText}`
      );
    }

    const tokens = await response.json();
    this.saveTokens(tokens);
    return tokens;
  }

  // Save tokens to localStorage
  private saveTokens(tokens: GoogleAuthToken): void {
    localStorage.setItem(this.tokenKey, JSON.stringify(tokens));
    const expiryTime = new Date().getTime() + tokens.expires_in * 1000;
    localStorage.setItem(this.expiryKey, expiryTime.toString());
  }

  // Get stored tokens
  getStoredTokens(): GoogleAuthToken | null {
    const tokensStr = localStorage.getItem(this.tokenKey);
    const expiryStr = localStorage.getItem(this.expiryKey);

    if (!tokensStr || !expiryStr) return null;

    const expiry = parseInt(expiryStr);
    if (new Date().getTime() > expiry) {
      this.clearTokens();
      return null;
    }

    return JSON.parse(tokensStr);
  }

  // Clear tokens
  clearTokens(): void {
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.expiryKey);
  }

  // Check if user has any connected Google accounts
  async isConnected(): Promise<boolean> {
    try {
      const { accounts } = await this.getConnectedAccounts();
      return accounts && accounts.length > 0;
    } catch (error) {
      console.error('Failed to check connection status:', error);
      return false;
    }
  }

  // Get current user from backend
  private async getCurrentUser() {
    const { userService } = await import('./api');
    try {
      return await userService.getCurrentUser();
    } catch (error) {
      console.error('Failed to get current user:', error);
      return null;
    }
  }

  // Refresh access token
  async refreshAccessToken(email?: string): Promise<GoogleAuthToken> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    let tokens = this.getStoredTokens();

    // If no tokens in localStorage, we can't refresh
    if (!tokens || !tokens.refresh_token) {
      throw new Error(
        'No refresh token available. Please reconnect your Google account.'
      );
    }

    const idToken = await user.getIdToken();

    const response = await fetch(`${this.API_URL}/auth/google/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        refreshToken: tokens.refresh_token,
        email: email || '',
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Token refresh failed:', errorText);
      if (response.status === 400 || response.status === 401) {
        throw new Error(
          'Token refresh failed. Please reconnect your Google account.'
        );
      }
      throw new Error('Failed to refresh token');
    }

    const newTokens = await response.json();
    // Update stored tokens with new values
    const updatedTokens = {
      ...tokens,
      ...newTokens,
      // Ensure refresh_token is preserved if not returned
      refresh_token: newTokens.refresh_token || tokens.refresh_token,
    };
    this.saveTokens(updatedTokens);
    return updatedTokens;
  }

  // Fetch recent emails from Gmail
  async fetchRecentEmails(email?: string): Promise<any> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const idToken = await user.getIdToken();

    const response = await fetch(`${this.API_URL}/auth/google/fetch-emails`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Fetch emails failed:', errorText);

      // Parse error message to provide better feedback
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.message?.includes('expired') ||
          errorData.message?.includes('token')
        ) {
          throw new Error(
            'Your Google account access has expired. Please reconnect your account.'
          );
        }
        if (errorData.message?.includes('No Google accounts connected')) {
          throw new Error(
            'No Google accounts connected. Please connect an account first.'
          );
        }
        throw new Error(errorData.message || 'Failed to fetch emails');
      } catch (parseError) {
        // If error text is not JSON, check status codes
        if (response.status === 401) {
          throw new Error(
            'Authentication failed. Please reconnect your Google account.'
          );
        }
        throw new Error('Failed to fetch emails. Please try again.');
      }
    }

    return response.json();
  }

  // Disconnect Google account
  async disconnect(email?: string): Promise<void> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const idToken = await user.getIdToken();

    await fetch(`${this.API_URL}/auth/google/disconnect`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({ email }),
    });

    // Only clear tokens if disconnecting all accounts (no email specified)
    if (!email) {
      this.clearTokens();
    }
  }

  // Get connected Google accounts
  async getConnectedAccounts(): Promise<any> {
    const user = auth.currentUser;
    if (!user) throw new Error('User not authenticated');

    const idToken = await user.getIdToken();

    const response = await fetch(
      `${this.API_URL}/auth/google/connected-accounts`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get connected accounts');
    }

    return response.json();
  }
}

export const googleAuthService = new GoogleAuthService();
